import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, Plus, Search, X } from "lucide-react";
import { Country } from "country-state-city";
import useDefaultCountryCode from "../../../../components/hook/use-default-country-code";
import { FetchProfileResult, TestRunSchemaField, TestRunSchemaResult, useFetchProfile } from "./journey-api";

// "Test your new bot journey" — collects a sample event for whichever
// journey this bot was started from, then builder-page.tsx calls the real
// POST /v1/bot/test-run/send with it. Fully driven by the live
// GET /v1/bot/test-run/schema?journey= (see journey-api.ts): every field
// shown here — "Customer details" on every journey, plus whatever that
// journey's own event needs standing in for (Loan details, Verification
// status, Card/Transaction details, ...) — comes from that response, not a
// fixed set, so a journey this builder has never seen before still renders
// correctly. `deliver` defaults OFF: with it off the backend still fetches a
// real profile and generates real AI content for the preview, it just
// doesn't dispatch anything — turning it on sends an actual
// WhatsApp/SMS/email to the phone/email typed in below, so it's opt-in and
// called out explicitly.

export interface TestRunData {
  // Keyed by the schema's own field `id` (e.g. "customer_name",
  // "mobile_number", "application_id", "loan_type", "day", ...) — the set of
  // keys varies per journey, so this stays a flexible map rather than a
  // fixed interface. `customer_name`/`mobile_number` are the two fields
  // every journey's schema has in common so far; builder-page.tsx reads
  // those two by that stable id.
  values: Record<string, string>;
  deliver: boolean;
}

export const DEFAULT_TEST_RUN_DATA: TestRunData = { values: {}, deliver: false };

const inputCls =
  "w-full h-[45px] px-3 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
    {children}
    {required && <span className="text-rose-500">*</span>}
  </label>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[11px] font-semibold tracking-wide text-gray-400 dark:text-gray-500 mb-3 mt-5 first:mt-0 uppercase">{children}</p>
);

const CURRENCY_SYMBOLS: Record<string, string> = { INR: "₹", USD: "$", NGN: "₦", GBP: "£", EUR: "€" };

// Same dial-code list + flag dropdown pattern used by Settings > Testing &
// Preview, Team > Invite and lead-qr-section.tsx (each keeps its own local
// copy rather than sharing one component) — kept in sync with that approach.
const normalizeDialCode = (phoneCode?: string | number | null) => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};

const countryCodeOptions = Country.getAllCountries()
  .map((c) => ({
    value: normalizeDialCode(c.phonecode),
    label: `${normalizeDialCode(c.phonecode)} ${c.isoCode}`,
    isoCode: c.isoCode,
    name: c.name,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

const getCountryByDialCode = (dialCode?: string | number | null) =>
  countryCodeOptions.find((c) => c.value === normalizeDialCode(dialCode));

const CountryCodeSelect: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const selected = getCountryByDialCode(value);
  // Matches the dial code ("234", "+91"), ISO code ("NG") or country name
  // ("Nigeria") — so nobody has to scroll ~250 entries to find theirs.
  const q = query.trim().toLowerCase().replace(/^\+/, "");
  const filtered = q
    ? countryCodeOptions.filter((o) => o.value.slice(1).startsWith(q) || o.isoCode.toLowerCase().startsWith(q) || o.name.toLowerCase().includes(q))
    : countryCodeOptions;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => {
          setOpen((s) => !s);
          setQuery("");
        }}
        className="flex h-[45px] items-center gap-1.5 rounded-l-lg bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-700 px-3 text-sm text-gray-700 dark:text-gray-200"
      >
        {selected && (
          <img
            src={`https://flagcdn.com/24x18/${selected.isoCode.toLowerCase()}.png`}
            alt=""
            className="h-3.5 w-5 rounded-sm object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <span>{value}</span>
        <ChevronDown size={14} className={`text-gray-400 dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-64 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <Search size={13} className="text-gray-400 shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code"
              className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
          {filtered.length === 0 && <p className="px-3 py-3 text-xs text-gray-400">No matching country.</p>}
          {filtered.map((option) => (
            <button
              key={option.isoCode}
              type="button"
              onClick={() => { onChange(option.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                option.value === value ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <img
                src={`https://flagcdn.com/24x18/${option.isoCode.toLowerCase()}.png`}
                alt=""
                className="h-3.5 w-5 rounded-sm object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              {option.label}
              <span className="ml-auto truncate text-xs text-gray-400">{option.name}</span>
            </button>
          ))}
          </div>
        </div>
      )}
    </div>
  );
};

// The one special-cased field type — a flag dropdown + local digits, same
// split Settings > Testing & Preview uses, rather than one free-text field.
// Defaults to the signed-in user's own country, and only while the user
// hasn't started typing a number yet.
// Shortest real local number we accept (most countries' subscriber numbers
// are 7–10 digits after the country code).
const MIN_LOCAL_DIGITS = 7;

const PhoneField: React.FC<{ field: TestRunSchemaField; value: string; onChange: (v: string) => void }> = ({ field, value, onChange }) => {
  const defaultCountryCode = useDefaultCountryCode("+234");
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [phoneDigits, setPhoneDigits] = useState("");
  // The schema's seeded `example` (e.g. "+123 - 9900990099") arrives
  // asynchronously, after this field has already mounted with nothing typed
  // — so it can't just be a useState initializer. Splits it into the two
  // pieces this field actually edits exactly once, the first time a non-empty
  // value shows up, and never again once the operator starts typing (or that
  // one-time sync has already happened) — otherwise the reconstructed
  // "+1239900990099" (no separator) this field itself produces below would
  // loop back in and re-split incorrectly.
  const touchedRef = useRef(false);
  const seededFromValueRef = useRef(false);
  useEffect(() => {
    if (touchedRef.current || seededFromValueRef.current || !value) return;
    const match = value.match(/^(\+\d+)\D*(\d+)$/);
    if (match) {
      seededFromValueRef.current = true;
      setCountryCode(match[1]);
      setPhoneDigits(match[2]);
    }
  }, [value]);

  useEffect(() => {
    setCountryCode((c) => (phoneDigits ? c : defaultCountryCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountryCode]);
  useEffect(() => {
    // Drop the local trunk "0" (Nigerian numbers are usually typed
    // 0810…) — "+234" + "08102814656" matches no warehouse profile, and the
    // run silently falls back to the demo profile (Ramesh Kumar).
    // A number still being typed (e.g. "+2346") is reported as empty, so the
    // required check keeps Save and Proceed disabled until it's complete — a
    // truncated phone used to go out as-is and resolve to no profile.
    const digits = phoneDigits.replace(/^0+/, "");
    onChange(digits.length >= MIN_LOCAL_DIGITS ? `${countryCode}${digits}` : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, phoneDigits]);

  return (
    <div className="mb-4">
      <Label required={field.required}>{field.label}</Label>
      <div className="flex items-center border rounded-lg h-[45px] bg-white dark:bg-gray-950 focus-within:ring-2 focus-within:ring-indigo-200 dark:focus-within:ring-indigo-500/20 border-gray-200 dark:border-gray-700">
        <CountryCodeSelect
          value={countryCode}
          onChange={(v) => {
            touchedRef.current = true;
            setCountryCode(v);
            setPhoneDigits("");
          }}
        />
        <input
          type="tel"
          value={phoneDigits}
          onChange={(e) => {
            touchedRef.current = true;
            setPhoneDigits(e.target.value.replace(/\D/g, ""));
          }}
          placeholder={field.example?.replace(/^\+\d+\s*-?\s*/, "") || "9900990099"}
          className="flex-1 h-full px-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
        />
      </div>
      {phoneDigits.replace(/^0+/, "").length > 0 && phoneDigits.replace(/^0+/, "").length < MIN_LOCAL_DIGITS && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Enter the full number — it looks cut short.</p>
      )}
    </div>
  );
};

// Per direct ask: a plain-text field standing in for a status/enum shouldn't
// make the operator type an exact value from memory. Most of these schemas'
// event-detail fields spell their valid values out in `hint` as
// "value1 | value2 | value3" before a sentence describing the field (e.g.
// kyc_status: "verified | pending | failed.", application_status: "started |
// in_progress | abandoned | submitted | rejected | expired | completed") —
// parsed generically here instead of hardcoding an options list per field
// per journey. A hint with no "|" (e.g. confidence's "0-1, how sure the
// derivation is") has nothing to parse and is left as plain text; a loose
// sanity check (each piece short, no run-on whitespace) guards against
// misreading an incidental "|" inside an unrelated sentence as an enum.
function parseEnumHint(hint?: string): string[] | null {
  if (!hint || !hint.includes("|")) return null;
  const firstSentence = hint.split(/\.\s|\.$/)[0];
  if (!firstSentence.includes("|")) return null;
  const parts = firstSentence
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2 || parts.some((p) => p.length > 24 || /\s{2,}/.test(p))) return null;
  return parts;
}

// Applies parseEnumHint across a field list, turning any plain-text field
// with an enum-shaped hint into a real dropdown. Fields that already have a
// type/options of their own (select, phone, currency, ...) are untouched.
function withEnumDropdowns(fields: TestRunSchemaField[]): TestRunSchemaField[] {
  return fields.map((f) => {
    if (f.type !== "text") return f;
    const options = parseEnumHint((f as any).hint);
    return options ? { ...f, type: "select", options } : f;
  });
}

// Forces a status field into a dropdown: the schema's own options first,
// then values spelled out in its hint ("a | b | c", or "a, b, c" / "a / b"),
// then `fallback`.
function asDropdown(f: TestRunSchemaField, fallback: string[]): TestRunSchemaField {
  if (f.options?.length) return { ...f, type: "select" };
  const hint = (f as any).hint as string | undefined;
  const loose = hint
    ?.split(/\.\s|\.$|:/)
    .map((piece) => piece.split(/\s*[|/,]\s*|\s+or\s+/).map((p) => p.trim()).filter(Boolean))
    .find((parts) => parts.length >= 2 && parts.every((p) => /^[\w-]{1,24}$/.test(p)));
  return { ...f, type: "select", options: parseEnumHint(hint) || loose || fallback };
}

// Used only when the airtime schema gives no values for the recharge status.
// Confirm these against the backend's airtime journey.
const RECHARGE_STATUS_FALLBACK = ["none", "pending", "failed", "completed"];

// INR and NGN (per direct ask), plus the signed-in user's own currency when
// it's neither. Defaulted from the user's own country, the same way
// PhoneField defaults its dial code: it keeps following that default (which
// lands only once /auth/get_userdata answers) until the operator picks a
// currency themselves. It's a closed set rather than sample data, so
// pre-selecting one doesn't risk sending someone else's placeholder value.
const CURRENCY_OPTIONS = ["INR", "NGN"];

const currencyForDialCode = (dialCode: string) => {
  const iso = getCountryByDialCode(dialCode)?.isoCode;
  return (iso && Country.getCountryByCode(iso)?.currency) || "NGN";
};

const CurrencyDropdownField: React.FC<{ field: TestRunSchemaField; value: string; onChange: (v: string) => void }> = ({ field, value, onChange }) => {
  const userCurrency = currencyForDialCode(useDefaultCountryCode("+234"));
  const touchedRef = useRef(false);
  useEffect(() => {
    if (!touchedRef.current && value !== userCurrency) onChange(userCurrency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCurrency]);
  const options = CURRENCY_OPTIONS.includes(userCurrency) ? CURRENCY_OPTIONS : [userCurrency, ...CURRENCY_OPTIONS];

  return (
    <div className="mb-4">
      <Label required={field.required}>{field.label}</Label>
      <select
        value={value}
        onChange={(e) => {
          touchedRef.current = true;
          onChange(e.target.value);
        }}
        className={inputCls}
      >
        {options.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </div>
  );
};

const isChurnSchema = (schema: TestRunSchemaResult | null) =>
  !!schema && (schema.journey === "churn_risk_escalation" || schema.event === "churn_risk_score_updated" || schema.event === "customer_churn_risk_threshold_crossed");

// Telco bots (data_balance_low, airtime_balance_zero, bill_overdue,
// subscriber_inactivity, …): the backend serves a per-bot Test Run schema
// (GET /v1/bot/test-run/schema) and asked for it to be rendered as served —
// no relabelling, no stand-in fields. Values post back flat under each
// field's own id, dots included (e.g. airtime_account.balance).
const isTelcoSchema = (schema: TestRunSchemaResult | null) =>
  !!schema && /data_balance|airtime|bill_overdue|inactivity|subscriber/.test(`${schema.journey} ${schema.event}`);

// No segment list from the backend (GET /v1/bot/condition-options has none).
const CHURN_SEGMENTS = ["Premium", "Standard", "Basic"];

// LTV — a money value with the user's own currency symbol in front.
const MoneyField: React.FC<{ field: TestRunSchemaField; value: string; onChange: (v: string) => void }> = ({ field, value, onChange }) => {
  const isIndia = useDefaultCountryCode("+234") === "+91";
  const symbol = isIndia ? "₹" : "₦";
  const group = (digits: string) => (digits ? Number(digits).toLocaleString(isIndia ? "en-IN" : "en-NG") : "");
  return (
    <div className="mb-4">
      <Label required={field.required}>{field.label}</Label>
      <input
        value={value ? `${symbol}${group(value)}` : ""}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        inputMode="numeric"
        placeholder={`${symbol}${group(String(field.example ?? ""))}`}
        className={inputCls}
      />
    </div>
  );
};

// Grouped the way each currency writes it (1,20,000 for INR, 120,000 for
// NGN); the stored value stays bare digits, which is what the engine reads.
const groupDigits = (digits: string, currency?: string) =>
  digits ? Number(digits).toLocaleString(currency === "INR" ? "en-IN" : "en-NG") : "";

const SchemaField: React.FC<{ field: TestRunSchemaField; value: string; onChange: (v: string) => void; currency?: string }> = ({
  field,
  value,
  onChange,
  currency,
}) => {
  if (field.type === "phone") return <PhoneField field={field} value={value} onChange={onChange} />;
  if (field.id === "currency") return <CurrencyDropdownField field={field} value={value} onChange={onChange} />;

  if (field.type === "money") return <MoneyField field={field} value={value} onChange={onChange} />;

  // A 0–1 score / threshold: digits and one decimal point only.
  if (field.id === "churn_score" || field.id === "threshold") {
    return (
      <div className="mb-4">
        <Label required={field.required}>{field.label}</Label>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"))}
          inputMode="decimal"
          placeholder={String(field.example ?? "")}
          className={inputCls}
        />
      </div>
    );
  }

  if (field.id === "card_last4") {
    return (
      <div className="mb-4">
        <Label required={field.required}>{field.label}</Label>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
          inputMode="numeric"
          placeholder={field.example}
          className={inputCls}
        />
      </div>
    );
  }

  if (field.type === "number" && (field.id === "amount" || field.id === "salary_amount" || field.id === "balance")) {
    return (
      <div className="mb-4">
        <Label required={field.required}>{field.label}</Label>
        <input
          value={groupDigits(value, currency)}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
          inputMode="numeric"
          placeholder={groupDigits(String(field.example ?? "").replace(/\D/g, ""), currency)}
          className={inputCls}
        />
      </div>
    );
  }

  if (field.type === "select") {
    const options = field.options || [];
    return (
      <div className="mb-4">
        <Label required={field.required}>{field.label}</Label>
        <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
          <option value="" disabled={field.required}>
            Select…
          </option>
          {options.map((o) => (
            <option key={o} value={o}>
              {field.optionLabels?.[o] ?? o}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "currency") {
    // Only show a symbol when the schema fixes this field's own currency
    // (e.g. loan_abandonment_recovery's application_amount: "INR") — when
    // currency is instead a separate selectable field alongside this one
    // (fraud_anomaly_alert's amount has no `currency` of its own), a fixed
    // symbol here would be wrong as soon as the operator picks a different one.
    const symbol = field.currency ? CURRENCY_SYMBOLS[field.currency] || field.currency : "";
    return (
      <div className="mb-4">
        <Label required={field.required}>{field.label}</Label>
        <div className="flex items-center border rounded-lg h-[45px] px-3 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700 focus-within:ring-2 focus-within:ring-indigo-200 dark:focus-within:ring-indigo-500/20">
          {symbol && <span className="text-sm text-gray-500 dark:text-gray-400 mr-1 shrink-0">{symbol}</span>}
          <input
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/[^\d,]/g, ""))}
            placeholder={field.example}
            className="flex-1 h-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
          />
        </div>
      </div>
    );
  }

  // "text" and any other/unrecognized field type — a plain input, matching
  // the schema's own field (not assumed to be one of the known special types).
  return (
    <div className="mb-4">
      <Label required={field.required}>{field.label}</Label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.example}
        maxLength={field.maxLength}
        className={inputCls}
      />
    </div>
  );
};

// Looks the typed phone up in the warehouse (POST /v1/bot/fetch-profile,
// debounced) and says who the run will actually send as — so a typo, or a
// number that isn't in the warehouse, shows before Save and Proceed rather
// than as a green run on the demo profile (Ramesh Kumar). Display only: the
// form submits exactly the same either way.
const ProfileMatchNotice: React.FC<{ phone: string }> = ({ phone }) => {
  const { fetchProfile } = useFetchProfile();
  const [state, setState] = useState<{ loading: boolean; result: FetchProfileResult | null; failed: boolean }>({ loading: false, result: null, failed: false });
  const lookupPhone = phone.replace(/\D/g, "").length >= 8 ? phone : "";

  useEffect(() => {
    if (!lookupPhone) {
      setState({ loading: false, result: null, failed: false });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      const result = await fetchProfile({ phone: lookupPhone });
      if (!cancelled) setState({ loading: false, result, failed: !result });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupPhone]);

  if (!lookupPhone) return null;
  if (state.loading && !state.result) {
    return (
      <p className="-mt-2 mb-4 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
        <Loader2 size={12} className="animate-spin" /> Looking this number up…
      </p>
    );
  }
  if (state.failed) return <p className="-mt-2 mb-4 text-xs text-gray-400 dark:text-gray-500">Couldn't check this number against the warehouse.</p>;
  if (!state.result) return null;

  const { profile, reason } = state.result;
  // Older responses without `matched`: a "golden" source is the real profile.
  const matched = state.result.matched ?? state.result.source === "golden";
  if (matched) {
    const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "this customer";
    const contact = [profile.phone || lookupPhone, profile.email].filter(Boolean).join(", ");
    return (
      <p className="-mt-2 mb-4 flex items-start gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 size={13} className="mt-px shrink-0" />
        <span>
          Will send as <span className="font-semibold">{name}</span>, {contact}
        </span>
      </p>
    );
  }
  return (
    <div className="-mt-2 mb-4 flex items-start gap-1.5 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
      <AlertTriangle size={13} className="mt-px shrink-0" />
      <span>
        <span className="font-semibold">This contact isn't in the warehouse</span> — the run would use a sample profile instead.
        {reason && <span className="block mt-0.5 text-amber-700/80 dark:text-amber-300/80">{reason}</span>}
      </span>
    </div>
  );
};

const TestRunModal: React.FC<{
  initial: TestRunData;
  schema: TestRunSchemaResult | null;
  schemaLoading: boolean;
  // Channels the built journey actually uses right now (builder-page.tsx's
  // collectActiveChannels) — GET /v1/bot/test-run/schema doesn't vary by
  // channel at all (confirmed live), so whether an Email field is added is
  // worked out client-side from this. The phone always shows regardless —
  // without it the backend falls back to the demo profile (see
  // effectiveSections below).
  channels: Set<string>;
  onClose: () => void;
  onSubmit: (data: TestRunData) => void;
}> = ({ initial, schema, schemaLoading, channels, onClose, onSubmit }) => {
  const [values, setValues] = useState<Record<string, string>>(initial.values);
  const [deliver, setDeliver] = useState(initial.deliver);
  const [showMore, setShowMore] = useState(false);
  const setField = (id: string, v: string) => setValues((d) => ({ ...d, [id]: v }));

  const hasEmailChannel = channels.has("email");

  // Same sections the schema returned, with a synthetic Email field added
  // when the journey actually sends email and the schema didn't already give
  // us one. Mobile number always stays, even on an email-only journey: it's
  // what finds the warehouse profile (Journey_Test_Run_Guide, 23 Oct 2026) —
  // an email alone falls back to the built-in demo profile (Ramesh Kumar,
  // rupees), which reads like a broken run.
  const effectiveSections = React.useMemo(() => {
    if (!schema) return [];
    const sections = schema.sections.map((s) => ({ ...s, fields: [...s.fields] }));
    const alreadyHasEmail = sections.some((s) => s.fields.some((f) => f.id === "email"));
    if (hasEmailChannel && !alreadyHasEmail) {
      const emailField: TestRunSchemaField = { id: "email", label: "Email", type: "text", required: true, example: "ada@example.com" };
      if (sections[0]) sections[0].fields = [...sections[0].fields, emailField];
      else sections.push({ section: "Customer details", fields: [emailField] });
    }
    // Dormancy reactivation classifies the customer's balance, but the schema
    // has no balance field (only days_inactive / last activity) — the design
    // asks for one under "Account details". Sent as `balance` + `currency`,
    // the same `currency` key the salary and fraud journeys use.
    const isDormancy = schema.journey === "dormancy_reactivation" || schema.event === "dormancy_flagged" || schema.event === "customer_dormancy_flagged";
    const hasBalance = sections.some((sec) => sec.fields.some((f) => f.id === "balance" || f.id === "amount"));
    if (isDormancy && !hasBalance) {
      sections.push({
        section: "Account details",
        fields: [
          { id: "currency", label: "Bal. Currency", type: "text", required: false },
          { id: "balance", label: "Bal. Amount", type: "number", required: false, example: 120000 },
        ],
      });
    }
    return sections;
  }, [schema, hasEmailChannel]);

  // Regroups effectiveSections for display — Customer details stays as-is; a
  // currency+amount pair (when present, under either `amount` or
  // `salary_amount`) gets its own side-by-side "Salary details" block;
  // everything else the event needs lands in one trailing "Other" bucket
  // instead of the schema's own (often journey-specific) section titles.
  //
  // Every remaining field is now shown, not just `select`-typed ones. That
  // used to be filtered down to "only what's actually read" when Test Run
  // called POST /v1/bot/test-run/send (which only ever consumed customer_
  // name/mobile_number/email) — but Test Run now calls POST /v1/bot/flows/run
  // with `data: <every field here>`, and the real engine reads whichever of
  // them a Condition node checks. Confirmed live: loan_abandonment_recovery's
  // only branch-driving field, `application_status`, is a plain `text` type
  // with no `select` twin the way salary_day_loan/new_account_welcome happen
  // to have one — the old filter would have hidden the one field needed to
  // test that journey's "Completed" path at all.
  const displaySections = React.useMemo(() => {
    if (effectiveSections.length === 0) return [] as (typeof effectiveSections[number] & { grid?: boolean; evenGrid?: boolean; collapsible?: boolean })[];
    const [customerSection, ...rest] = effectiveSections;

    // Telco designs that group the schema's own fields under a titled block:
    //  - data_balance_low: "Data balance" = current balance + threshold, side by side
    //  - subscriber_inactivity: "In-activity info" = in-activity days
    // Layout only — each slot is a schema field matched by id (dotted ids
    // included) and nothing is added when the schema doesn't serve it. Every
    // other field stays reachable under "Other" / "More details".
    const journeyKey = `${schema?.journey} ${schema?.event}`;
    const telcoLayout = !isTelcoSchema(schema)
      ? null
      : /data_balance_low/.test(journeyKey)
      ? {
          section: "Data balance",
          slots: [
            { match: /threshold/i, label: "Threshold" },
            { match: /data_balance|balance_mb|balance/i, label: "Current data balance" },
          ],
          order: ["Current data balance", "Threshold"],
        }
      : /inactivity/.test(journeyKey)
      ? { section: "In-activity info", slots: [{ match: /inactiv/i, label: "In-activity days" }], order: ["In-activity days"] }
      : null;
    // airtime_balance_zero (design): Customer details only. The balance is
    // zero by definition, so the only extra field kept is one the run can't
    // do without — a required, non-profile field such as
    // recharge.pending_recharge_status, shown as a dropdown. Optional and
    // profile-filled fields are dropped; the backend fills those itself.
    if (isTelcoSchema(schema) && /airtime/.test(journeyKey)) {
      const needed = rest
        .flatMap((sec) => sec.fields)
        .filter((f) => f.required && f.source !== "warehouse")
        .map((f) => (/recharge.*status|status.*recharge/i.test(f.id) ? asDropdown(f, RECHARGE_STATUS_FALLBACK) : withEnumDropdowns([f])[0]))
        // "Recharge.pending recharge status" → "Pending recharge status": the
        // dotted prefix is already the section title.
        .map((f) => {
          const label = f.label.includes(".") ? f.label.split(".").pop()!.trim() : f.label;
          return { ...f, label: label.charAt(0).toUpperCase() + label.slice(1) };
        });
      return [customerSection, ...(needed.length ? [{ section: "Recharge", fields: needed }] : [])];
    }

    if (telcoLayout) {
      const restFields = rest.flatMap((sec) => sec.fields);
      const taken = new Set<TestRunSchemaField>();
      const picked: TestRunSchemaField[] = [];
      // Slots are matched in the listed order (threshold before balance, so
      // "balance_threshold_mb" isn't taken as the balance), then shown in `order`.
      telcoLayout.slots.forEach((slot) => {
        const f = restFields.find((x) => !taken.has(x) && slot.match.test(x.id));
        if (!f) return;
        taken.add(f);
        picked.push({ ...f, label: slot.label, type: "number", required: f.source === "warehouse" ? false : f.required } as TestRunSchemaField);
      });
      picked.sort((a, b) => telcoLayout.order.indexOf(a.label) - telcoLayout.order.indexOf(b.label));
      const others = withEnumDropdowns(restFields.filter((f) => !taken.has(f)));
      const requiredLeft = others.filter((f) => f.required && f.source !== "warehouse");
      const optionalLeft = others.filter((f) => !f.required || f.source === "warehouse").map((f) => ({ ...f, required: false }));
      return [
        customerSection,
        ...(picked.length ? [{ section: telcoLayout.section, evenGrid: picked.length === 2, fields: picked }] : []),
        ...(requiredLeft.length ? [{ section: "Other", fields: requiredLeft }] : []),
        ...(optionalLeft.length ? [{ section: "More details", fields: optionalLeft, collapsible: true }] : []),
      ];
    }

    // Other telco bots: the schema's own sections, in order. `source:
    // "warehouse"` fields are optional — left blank, the profile fills them.
    if (isTelcoSchema(schema)) {
      return [
        customerSection,
        ...rest.map((sec) => ({
          ...sec,
          fields: withEnumDropdowns(sec.fields).map((f) => (f.source === "warehouse" ? { ...f, required: false } : f)),
        })),
      ];
    }

    // Churn risk escalation (design): Churn risk = current score + threshold,
    // Customer value = LTV + segment. The schema's churn_band / previous_band
    // move under "More details" — churn_band is filled from the score on
    // submit when left blank (see submittedValues).
    if (isChurnSchema(schema)) {
      const restFields = rest.flatMap((sec) => sec.fields);
      // The schema's own field wins once it serves one (backend, 28 Oct: score
      // + LTV + segment per journey); the stand-in only covers its absence.
      const DESIGN_IDS = ["churn_score", "threshold", "ltv", "customer_segment"];
      const fromSchema = (id: string, fallback: Partial<TestRunSchemaField>, label: string) => {
        const f = restFields.find((x) => x.id === id);
        return { ...fallback, ...f, id, label } as TestRunSchemaField;
      };
      const others = restFields.filter((f) => !DESIGN_IDS.includes(f.id)).map((f) => ({ ...f, required: false }));
      return [
        customerSection,
        {
          section: "Churn risk",
          evenGrid: true,
          fields: [
            fromSchema("churn_score", { type: "number", required: false, example: "0.78" }, "Current score"),
            fromSchema("threshold", { type: "number", required: false, example: "0.70" }, "Threshold"),
          ],
        },
        {
          section: "Customer value",
          evenGrid: true,
          fields: [
            // LTV keeps the money input (₦/₹ symbol) whatever type the schema says.
            { ...fromSchema("ltv", { required: false, example: "250000" }, "LTV"), type: "money" } as TestRunSchemaField,
            (() => {
              const seg = fromSchema("customer_segment", { required: false }, "Segment");
              return { ...seg, type: "select", options: seg.options?.length ? seg.options : CHURN_SEGMENTS } as TestRunSchemaField;
            })(),
          ],
        },
        ...(others.length ? [{ section: "More details", fields: withEnumDropdowns(others), collapsible: true }] : []),
      ];
    }

    // Current schema shape (confirmed live on new_account_welcome,
    // loan_abandonment_recovery and salary_day_loan): a "What this journey
    // decides on" section whose fields carry `source`. Per the backend's
    // Test Run guide the form is then just phone + email + the `source:
    // "you"` dropdown(s) — the event-detail sections, customer_name and every
    // `source: "warehouse"` field are left out so the fetched profile answers
    // them (a typed name or a pre-filled currency would override the real
    // warehouse value instead of testing it).
    const decisionSection = rest.find((s) => s.fields.some((f) => f.source));
    if (decisionSection) {
      const pick = decisionSection.fields
        .filter((f) => f.source === "you")
        .map((f) => ({
          ...f,
          label: f.label.replace(/\bKyc\b/, "KYC"),
          // Display only — the submitted value is still the schema's own
          // option. "Application completed?" checks for "Completed"; anything
          // else is the abandoned path, which is what the guide calls it.
          optionLabels: f.id === "application_status" ? { "Not Clicked": "Abandoned" } : f.optionLabels,
        }));
      // Customer name stays visible but optional: typed, it's sent and used;
      // left blank it's dropped (see submittedValues) so the warehouse
      // profile's own name answers instead.
      const customerFields = customerSection.fields.map((f) =>
        f.id === "customer_name" ? { ...f, required: false, example: "Leave blank to use the profile's name" } : f
      );
      return [{ ...customerSection, fields: customerFields }, ...(pick.length ? [{ ...decisionSection, fields: pick }] : [])];
    }

    // Per direct ask: any plain-text status field (kyc_status, bvn_status,
    // kyc_verified, loan_product_type, ...) becomes a real dropdown here,
    // applied once up front so every section built below already has the
    // converted fields — see withEnumDropdowns/parseEnumHint above.
    const restFields = withEnumDropdowns(rest.flatMap((s) => s.fields));
    const amountField = restFields.find((f) => f.id === "amount" || f.id === "salary_amount" || f.id === "balance");
    const currencyField = restFields.find((f) => f.id === "currency");
    const consumedIds = new Set<string>();
    // Confirmed live on salary_day_loan: `amount` and `salary_amount` are
    // two separate schema fields carrying the literal same value ("Same
    // value as amount, under the name Abbas's trigger panel uses"). Only one
    // of them lands in the grid above, so both ids need consuming here —
    // otherwise the other one leaks into "Other" as a duplicate "Salary
    // amount" field with the same placeholder as the real one.
    if (amountField) {
      consumedIds.add(amountField.id);
      consumedIds.add("amount");
      consumedIds.add("salary_amount");
    }
    if (currencyField) consumedIds.add(currencyField.id);

    const sections: (typeof effectiveSections[number] & { grid?: boolean; evenGrid?: boolean; collapsible?: boolean })[] = [customerSection];

    // Card journeys (fraud_anomaly_alert) — the design gives the card its own
    // block ahead of the transaction, so the alert names a card the
    // customer recognises.
    const cardField = restFields.find((f) => f.id === "card_last4");
    if (cardField) {
      consumedIds.add(cardField.id);
      sections.push({ section: "Card details", fields: [{ ...cardField, label: "Card last 4 digits" }] });
    }

    if (amountField && currencyField) {
      const isSalary = amountField.id === "salary_amount" || restFields.some((f) => f.id === "salary_amount") || !!schema?.journey?.includes("salary");
      const title = amountField.id === "balance" ? "Account details" : isSalary ? "Salary details" : "Transaction details";
      sections.push({ section: title, fields: [currencyField, amountField], grid: true });
    }

    // Optional leftovers (merchant, channel, country, ...) aren't in the
    // design — tucked behind "More details" rather than dropped, since a
    // Condition or the template may still read them.
    const remaining = restFields.filter((f) => !consumedIds.has(f.id));
    const requiredLeft = remaining.filter((f) => f.required);
    const optionalLeft = remaining.filter((f) => !f.required);
    if (requiredLeft.length) sections.push({ section: "Other", fields: requiredLeft });
    if (optionalLeft.length) sections.push({ section: "More details", fields: optionalLeft, collapsible: true });
    return sections;
  }, [effectiveSections, schema]);

  // Fields start empty — `example` is shown only as a placeholder (see
  // SchemaField below), not pre-filled into the real value, so the operator
  // always types a real test phone/email rather than silently sending to
  // whatever sample data the schema happens to suggest.
  const allFields = displaySections.flatMap((s) => s.fields);
  const canSubmit = allFields.every((f) => !f.required || (values[f.id] || "").trim().length > 0);
  // Blank fields (and anything left over in `values` from an earlier run but
  // no longer shown) are dropped rather than sent as "" — a key present with
  // an empty value would stop the warehouse from answering it.
  const submittedValues = () => {
    const out: Record<string, string> = Object.fromEntries(allFields.map((f) => [f.id, (values[f.id] || "").trim()]).filter(([, v]) => v));
    // churn_band is required by the schema but not in the design — derived
    // from the score against the threshold (0.7 when none is typed).
    if (isChurnSchema(schema) && !out.churn_band && out.churn_score) {
      const score = Number(out.churn_score);
      const threshold = Number(out.threshold || "0.7");
      out.churn_band = score >= threshold ? "High" : score >= threshold / 2 ? "Medium" : "Low";
    }
    return out;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-gray-50 dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 pt-6">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{schema?.title || "Test your new bot journey"}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Add details to test your new bot journey</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 shrink-0 mt-1">
            <X size={22} />
          </button>
        </div>

        <div className="px-6 pb-2 pt-6">
          {schemaLoading && !schema ? (
            <p className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 py-8 justify-center">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </p>
          ) : (
            displaySections.map((section) =>
              section.collapsible && !showMore ? (
                <button
                  key={section.section}
                  type="button"
                  onClick={() => setShowMore(true)}
                  className="mb-4 flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700"
                >
                  <Plus size={15} /> Add more details <span className="font-normal text-gray-400">(optional)</span>
                </button>
              ) : (
              <React.Fragment key={section.section}>
                <SectionLabel>{section.section}</SectionLabel>
                {section.description && <p className="-mt-2 mb-3 text-xs text-gray-500 dark:text-gray-400">{section.description}</p>}
                {section.grid || section.evenGrid ? (
                  // Currency narrower than the amount, as in the design;
                  // churn's score/threshold and LTV/segment are even.
                  <div className={`grid gap-4 ${section.evenGrid ? "grid-cols-2" : "grid-cols-[2fr_3fr]"}`}>
                    {section.fields.map((field) => (
                      <SchemaField key={field.id} field={field} value={values[field.id] || ""} onChange={(v) => setField(field.id, v)} currency={values.currency} />
                    ))}
                  </div>
                ) : (
                  section.fields.map((field) => (
                    <React.Fragment key={field.id}>
                      <SchemaField field={field} value={values[field.id] || ""} onChange={(v) => setField(field.id, v)} />
                      {field.id === "mobile_number" && <ProfileMatchNotice phone={values.mobile_number || ""} />}
                      {/* Telco schemas: the field's own hint, and a note on
                          warehouse fields that blank means "use the profile". */}
                      {isTelcoSchema(schema) && (field.source === "warehouse" || (field.hint && !parseEnumHint(field.hint))) && (
                        <p className="-mt-2 mb-4 text-xs text-gray-400 dark:text-gray-500">
                          {[field.hint && !parseEnumHint(field.hint) ? field.hint : "", field.source === "warehouse" ? "Optional — leave blank to use the profile's value." : ""]
                            .filter(Boolean)
                            .join(" ")}
                        </p>
                      )}
                    </React.Fragment>
                  ))
                )}
              </React.Fragment>
              )
            )
          )}
        </div>

        <div className="px-6 pb-2">
          <label className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 cursor-pointer">
            <input type="checkbox" checked={deliver} onChange={(e) => setDeliver(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            <span className="text-xs text-amber-800 dark:text-amber-300">
              <span className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle size={13} /> Actually send this test
              </span>
              Off by default — the run still fetches a real profile and generates real AI content to preview, it just isn't dispatched. Turning this on sends a real WhatsApp/SMS/email to the number/address above.
            </span>
          </label>
        </div>

        <div className="px-6 py-6 pt-2">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSubmit({ values: submittedValues(), deliver })}
            className="w-full py-3.5 text-base font-medium text-white bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {schema?.submit_label || "Save and Proceed"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestRunModal;
